import { useMemo } from "react";
import { createListCollection } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { useDashboardStoreActions, useSelectedTimeRange } from "@/features/dashboard/store";
import { useWidgetHostSettings } from "@/features/shared/widget-context/widget-context-host";
import Select, { type SelectOption } from "@/components/select/select";
import Button from "@/components/button/button";
import { Icon } from "@/components/icon/icon";
import Popover from "@/components/popover/popover";

import styles from "./time-range-control.module.css";

/** Human label for an hour offset relative to now: "Now", "{n} hours", or whole days. */
function formatDurationLabel(
  hours: number,
  t: (key: string, opts?: object) => string,
): string {
  if (hours === 0) return t("common.now");
  if (hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? t("common.days_singular", { days }) : t("common.days", { days });
  }
  return t("common.hours", { hours });
}

/** Compact summary for the trigger button, e.g. "-24h" / "Now" / "+6h". */
function formatOffsetSummary(
  hours: number | undefined,
  t: (key: string, opts?: object) => string,
): string | undefined {
  if (hours === undefined) return undefined;
  if (hours === 0) return t("common.now");
  return `${hours > 0 ? "+" : ""}${hours}h`;
}

/**
 * From/to time-range picker for the CMS toolbar, modeled after the dashboard
 * time selector: a compact trigger button shows the current range and opens a
 * popover with the two selects. Reads the selectable options from the
 * surrounding {@link WidgetContextHost} and writes the scoped time range through
 * the runtime context. The trigger stays disabled until options load.
 */
export function TimeRangeControl() {
  const { t } = useTranslation();
  const { timeRangeSettings } = useWidgetHostSettings();
  const { setTimeRange } = useDashboardStoreActions();
  const { hoursStart, hoursEnd } = useSelectedTimeRange();

  const startCollection = useMemo(
    () =>
      createListCollection<SelectOption>({
        items: (timeRangeSettings?.start_options ?? []).map((hours) => ({
          label: formatDurationLabel(hours, t),
          value: hours.toString(),
        })),
      }),
    [timeRangeSettings, t],
  );
  const endCollection = useMemo(
    () =>
      createListCollection<SelectOption>({
        items: (timeRangeSettings?.end_options ?? []).map((hours) => ({
          label: formatDurationLabel(hours, t),
          value: hours.toString(),
        })),
      }),
    [timeRangeSettings, t],
  );

  const summary = useMemo(() => {
    const start = formatOffsetSummary(hoursStart, t);
    const end = formatOffsetSummary(hoursEnd, t);
    if (!start || !end) return undefined;
    return `${start} ${t("common.to")} ${end}`;
  }, [hoursStart, hoursEnd, t]);

  return (
    <Popover
      nested
      trigger={(context) => (
        <Button variant="primary" disabled={!timeRangeSettings}>
          <Icon name="clock" size={16} />
          {summary && <span>{summary}</span>}
          <Icon name={context.open ? "chevron-up" : "chevron-down"} size={16} />
        </Button>
      )}
    >
      <div className={styles.panel}>
        <Select
          collection={startCollection}
          value={hoursStart?.toString()}
          onValueChange={(value) => setTimeRange({ startHour: Number(value) })}
          disabled={!timeRangeSettings}
          label={t("common.from")}
          placeholder={t("common.from")}
        />
        <Select
          collection={endCollection}
          value={hoursEnd?.toString()}
          onValueChange={(value) => setTimeRange({ endHour: Number(value) })}
          disabled={!timeRangeSettings}
          label={t("common.to")}
          placeholder={t("common.to")}
        />
      </div>
    </Popover>
  );
}
